import { env } from '../../lib/env'
import { isDevEnvironment, isLocalDevWithoutSso, isSuiteSsoRedirectMode } from '../sso-flags'
import { SUITE_PAY_REFRESH_TOKEN_QUERY_PARAM } from '../sso-constants'

const PREFIX = '[SSO Pay]'

function timestamp(): string {
  return new Date().toISOString()
}

function formatError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    }
  }
  return { raw: error }
}

/** Logs JWT / refresh token in full while developing (NODE_ENV=development). */
export function logToken(label: string, token: string | null | undefined): void {
  const value = token?.replaceAll('"', '') ?? ''
  console.log(`${PREFIX} [token] ${label}`, {
    at: timestamp(),
    present: Boolean(value),
    length: value.length,
    fullToken: value || '(empty)'
  })
}

export function logSessionSnapshot(context: string): void {
  const params = new URLSearchParams(window.location.search)
  const sessionToken = globalThis.sessionStorage.getItem('token')
  const localToken = globalThis.localStorage.getItem('token')

  console.group(`${PREFIX} [session] ${context}`)
  console.log('at', timestamp())
  console.log('location', {
    href: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search
  })
  console.log('queryParams', {
    pay_rt: params.get(SUITE_PAY_REFRESH_TOKEN_QUERY_PARAM),
    refreshToken: params.get('refreshToken'),
    refresh_token: params.get('refresh_token'),
    all: Object.fromEntries(params.entries())
  })
  console.log('storage.sessionStorage.token', sessionToken)
  console.log('storage.localStorage.token', localToken)
  logToken('sessionStorage.token (parsed)', sessionToken)
  logToken('localStorage.token (parsed)', localToken)
  console.log('storage.isSync', globalThis.sessionStorage.getItem('isSync'))
  console.log('storage.userSession', globalThis.sessionStorage.getItem('userSession'))
  console.log('document.cookie (readable only)', document.cookie || '(empty or httpOnly only)')
  console.log('flags', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_NODE_ENV: process.env.REACT_APP_NODE_ENV,
    REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV_raw: process.env.REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV,
    REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV_parsed: env.REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV,
    isDevEnvironment: isDevEnvironment(),
    isLocalDevWithoutSso: isLocalDevWithoutSso(),
    isSuiteSsoRedirectMode: isSuiteSsoRedirectMode()
  })
  console.log('env (parsed)', {
    REACT_APP_ADAMO_API_BASE_URL: env.REACT_APP_ADAMO_API_BASE_URL,
    REACT_APP_ADAMO_LANDING_BASE_URL: env.REACT_APP_ADAMO_LANDING_BASE_URL,
    REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV: env.REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV
  })
  console.groupEnd()
}

export function logInfo(step: string, details?: Record<string, unknown>): void {
  console.log(`${PREFIX} ${step}`, { at: timestamp(), ...details })
}

export function logWarn(step: string, details?: Record<string, unknown>): void {
  console.warn(`${PREFIX} ${step}`, { at: timestamp(), ...details })
}

export function logError(step: string, error: unknown, details?: Record<string, unknown>): void {
  console.error(`${PREFIX} ${step}`, { at: timestamp(), error: formatError(error), ...details })
}

export function logRedirect(reason: string, targetUrl: string, details?: Record<string, unknown>): void {
  console.warn(`${PREFIX} [redirect] ${reason}`, {
    at: timestamp(),
    targetUrl,
    currentHref: window.location.href,
    ...details
  })
}

export function logApiRequest(method: string, url: string, details?: Record<string, unknown>): void {
  console.log(`${PREFIX} [api] → ${method} ${url}`, { at: timestamp(), ...details })
}

export function logApiResponse(method: string, url: string, status: number, details?: Record<string, unknown>): void {
  console.log(`${PREFIX} [api] ← ${method} ${url} ${status}`, { at: timestamp(), ...details })
}

/** Call once when the app bundle loads (before React). */
export function logSsoBootstrapOnLoad(): void {
  console.log(`${PREFIX} ═══════════════════════════════════════`)
  console.log(`${PREFIX} App bundle loaded — SSO diagnostics enabled`)
  logSessionSnapshot('initial page load')
  console.log(`${PREFIX} ═══════════════════════════════════════`)
}
